import React from "react";
import { NAV_ACCESS, ITE_EVENTS, IMPERSONATION_ROLES } from "../data/mockData";
import { useApp } from "../context/AppContext";
import { colors } from "../theme";
import NotifBell from "./NotifBell";
import { Calendar, Landmark, Building2, ClipboardList, BarChart2, TrendingUp, Search, Upload, Settings, Briefcase, Target, Megaphone, Wrench, LayoutDashboard } from "lucide-react";
import Logo from "./Logo";

const F = "'Montserrat',sans-serif";

export default function Sidebar({
  active, onNav, selectedEvent, onChangeEvent,
  notifs, setNotifs, notifOpen, setNotifOpen,
  currentRole, setCurrentRole,
}) {
  const { setSelectedEvent } = useApp();

  const SectionHeader = ({ label }) => (
    <div style={{
      fontSize:9, fontWeight:700, color:colors.text.muted,
      textTransform:"uppercase", letterSpacing:0.6,
      padding:"10px 12px 4px",
    }}>{label}</div>
  );

  const NavItem = ({ id, Icon, label, indent }) => {
    const a = active === id;
    return (
      <div
        onClick={() => { if (id === "portfolio") setSelectedEvent(null); onNav(id); }}
        style={{
          display:"flex", alignItems:"center", gap:8,
          padding:"8px 12px", paddingLeft: indent ? 24 : 12,
          borderRadius:6, background: a ? colors.sidebar.activeBg : "transparent",
          color: a ? colors.sidebar.activeText : colors.sidebar.text, cursor:"pointer",
          fontSize:12, fontWeight: a ? 600 : 400,
          marginBottom:2, transition:"all 0.15s",
        }}
        onMouseEnter={e => { if (!a) e.currentTarget.style.background = colors.sidebar.hoverBg; }}
        onMouseLeave={e => { if (!a) e.currentTarget.style.background = "transparent"; }}
      >
        <span style={{ width:18, textAlign:"center", display:"flex", alignItems:"center", justifyContent:"center" }}>{Icon && <Icon size={14} />}</span>{label}
      </div>
    );
  };

  const allowed = NAV_ACCESS[currentRole] || Object.keys(NAV_ACCESS).flatMap(r => NAV_ACCESS[r]);
  const can = (id) => allowed.includes(id);

  const TRACK = [
    { id:"portfolio", Icon: Landmark,   label:"Competitor Shows" },
    { id:"companies", Icon: Building2,  label:"Companies"        },
  ];
  const INSIGHTS = [
    { id:"insights",  Icon: TrendingUp,    label:"Market Insights" },
    { id:"growth",    Icon: BarChart2,     label:"Growth Trends"   },
    { id:"pipeline",  Icon: BarChart2,     label: currentRole === "Marketing Head" ? "Campaign Pipeline" : "Pipeline" },
  ];
  const UTIL = [
    { id:"leads",   Icon: ClipboardList, label:"Lead Review"   },
    { id:"quality", Icon: Search,        label:"Data Quality"  },
    { id:"send",    Icon: Upload,        label:"Send & Export" },
    { id:"admin",   Icon: Settings,      label:"Admin"         },
  ];

  const roleIcons = {
    "Sales Head": Briefcase,
    "Sales Manager": ClipboardList,
    "Marketing Head": Target,
    "Marketing Manager": Megaphone,
    "Data Admin": Wrench,
    "Management": BarChart2,
  };
  const currentEvent = ITE_EVENTS.find(e => e.id === selectedEvent);

  return (
    <div style={{
      width:210, background:colors.sidebar.bg, borderRight:`1px solid ${colors.sidebar.border}`,
      display:"flex", flexDirection:"column",
      padding:"14px 10px", flexShrink:0, overflow:"hidden",
    }}>
      <div style={{
        display:"flex", alignItems:"center", gap:8,
        padding:"0 8px 16px", borderBottom:`1px solid ${colors.sidebar.border}`, marginBottom:14,
      }}>
        <Logo size={28} />
        <div style={{ flex:1 }}>
          <div style={{ color:colors.text.heading, fontWeight:700, fontSize:13 }}>Compass</div>
          <div style={{ color:colors.text.muted, fontSize:8, letterSpacing:0.8 }}>Competitor Intelligence</div>
        </div>
      </div>

      {can("dashboard") && <NavItem id="dashboard" Icon={LayoutDashboard} label="Dashboard" />}

      <SectionHeader label="Track" />
      {TRACK.filter(t => can(t.id)).map(t => <NavItem key={t.id} id={t.id} Icon={t.Icon} label={t.label} />)}

      <SectionHeader label="Insights" />
      {INSIGHTS.filter(t => can(t.id)).map(t => <NavItem key={t.id} id={t.id} Icon={t.Icon} label={t.label} />)}

      {UTIL.filter(u => can(u.id)).length > 0 && (
        <>
          <SectionHeader label="Workspace" />
          {UTIL.filter(u => can(u.id)).map(u => <NavItem key={u.id} id={u.id} Icon={u.Icon} label={u.label} />)}
        </>
      )}

      <div style={{ flex:1 }} />

      <div style={{ padding:"10px", background:colors.surface.subtle, borderRadius:8, marginTop:8, marginBottom:6 }}>
        {IMPERSONATION_ROLES.includes(currentRole) ? (<>
          <div style={{ fontSize:9, fontWeight:600, color:colors.text.muted, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>View As</div>
          <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
            {Object.keys(roleIcons).map(role => {
              const isActive = currentRole === role;
              const RoleIcon = roleIcons[role];
              return (
                <button key={role} onClick={() => setCurrentRole(role)} style={{
                  padding:"5px 8px", borderRadius:5, border:"none",
                  background: isActive ? colors.brand.primaryTint : "transparent",
                  color: isActive ? colors.brand.primary : colors.text.muted,
                  fontSize:10, fontWeight: isActive ? 700 : 400,
                  cursor:"pointer", fontFamily:F, textAlign:"left",
                  display:"flex", alignItems:"center", gap:6, transition:"all 0.1s",
                }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = colors.sidebar.hoverBg; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                >
                  <span style={{ display:"flex", alignItems:"center" }}>{RoleIcon && <RoleIcon size={11} />}</span>{role}
                  {isActive && <span style={{ marginLeft:"auto", width:6, height:6, borderRadius:3, background:colors.brand.primary }} />}
                </button>
              );
            })}
          </div>
        </>) : (
          /* Real users see their own role, not an impersonation switcher */
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ display:"flex", alignItems:"center", color:colors.brand.primary }}>
              {roleIcons[currentRole] && React.createElement(roleIcons[currentRole], { size:12 })}
            </span>
            <div>
              <div style={{ fontSize:9, fontWeight:600, color:colors.text.muted, textTransform:"uppercase", letterSpacing:0.5 }}>Signed in as</div>
              <div style={{ fontSize:11, fontWeight:700, color:colors.text.heading }}>{currentRole}</div>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding:"10px", background:colors.surface.subtle, borderRadius:8 }}>
        <div style={{ fontSize:9, fontWeight:600, color:colors.text.muted, textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>Current Context</div>
        {currentEvent ? (
          <>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
              <div style={{ width:20, height:20, borderRadius:4, background: currentEvent.color, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <span style={{ fontSize:8, fontWeight:800, color:"#fff" }}>ITE</span>
              </div>
              <div>
                <div style={{ fontSize:8, color:colors.text.muted }}>ITE Event</div>
                <div style={{ fontSize:10, color:colors.text.heading, fontWeight:600 }}>{currentEvent.name}</div>
              </div>
            </div>
            <div onClick={onChangeEvent} style={{
              padding:"4px 8px", background:colors.surface.hover, borderRadius:4,
              fontSize:9, color:colors.text.body, cursor:"pointer", textAlign:"center", fontWeight:500,
            }}>Change Event</div>
          </>
        ) : (
          <>
            <div style={{ fontSize:10, color:colors.text.muted, marginBottom:4 }}>⊘ No event selected</div>
            <div style={{ fontSize:9, color:colors.text.faint }}>Select an event to start.</div>
          </>
        )}
      </div>
    </div>
  );
}
